import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TopBar from "./TopBar";
import FilterBar from "./FilterBar";
import MACFilter from "./MACFilter";
import PositionTable from "./PositionTable";
import NewPositionDrawer from "./NewPositionDrawer";
import { getPositions, deletePosition } from "../../../../../api/positions";
import { getMacsWithSows } from "../../../../../api/macs";
import { setUblCeiling, getUblPoolStatus } from "../../../../../api/ublPool";
import PageInfo from "../../../../../components/common/PageInfo";
import { ExportButton } from "../../../../../components/common/Buttons";
import { exportPositions } from "./exportPositions";

const EMPTY_FILTERS = {
  type: null,
  status: null,
  ublStatus: null,
  quarter: null,
};

const getCurrentQuarterStr = () => {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  const fy = now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
  return `Q${q}-${fy}`;
};

const PositionIDManagement = ({ readOnly = false, macsList = null, singleMacMode = false }) => {
  const theme = useTheme();
  const t = theme.custom;

  const [selectedMAC, setSelectedMAC] = useState(macsList?.length === 1 ? macsList[0].macId : null);
  const [selectedSOW, setSelectedSOW] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [cap, setCap] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [macs, setMacs] = useState([]);
  const [macsLoading, setMacsLoading] = useState(true);

  // Drawer state — null = closed, undefined = create mode, object = edit mode
  const [drawerRow, setDrawerRow] = useState(null);
  const drawerOpen = drawerRow !== null;
  // Add new state
  const [totalCap, setTotalCap] = useState(null);

  // Helper to generate all quarter strings for current FY
  const getFYQuarters = () => {
    const now = new Date();
    const fy = now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
    return [`Q1-${fy}`, `Q2-${fy}`, `Q3-${fy}`, `Q4-${fy}`];
  };

  // Replace the existing pool status useEffect with this:
  useEffect(() => {
    setStatsLoading(true);

    const currentQuarter = getCurrentQuarterStr();

    // Fetch current quarter for stats + cap setter
    const currentQ$ = getUblPoolStatus(currentQuarter)
      .then(applyPoolStatus)
      .catch((err) => {
        if (err.response?.status !== 404)
          console.error("Failed to load pool status:", err);
      });

    // Fetch all quarters and sum ceilings for the left banner total
    const allQ$ = Promise.allSettled(
      getFYQuarters().map((q) => getUblPoolStatus(q)),
    ).then((results) => {
      const total = results.reduce((sum, r) => {
        if (r.status === "fulfilled" && r.value?.ceiling) {
          return sum + r.value.ceiling;
        }
        return sum;
      }, 0);
      setTotalCap(total);
    });

    Promise.all([currentQ$, allQ$]).finally(() => setStatsLoading(false));
  }, []);

  const applyPoolStatus = (data) => {
    
    setStats({
      unbilledHC: data.total,
      allocToMAC: data.deployed,
      inSCGPool: data.bench,
      impactTrainees: data.impactors || 0,
      forwardClaimed: data.forwardClaimed || 0,
    });
    
    setCap({
      ceiling: data.ceiling,
      setByUserName: data.setByName,
      setAt: data.setAt,
    });
  };

//   useEffect(() => {
//   setStatsLoading(true);
//   getUblPoolStatus()                        // ← no quarter arg
//     .then((data) => {
//       applyPoolStatus(data);
//       setTotalCap(data.ceiling);            // ← ceiling is already global
//     })
//     .catch((err) => {
//       if (err.response?.status !== 404)
//         console.error("Failed to load pool status:", err);
//     })
//     .finally(() => setStatsLoading(false));
// }, []);

  useEffect(() => {
    if (macsList) {
      setMacs(macsList);
      setMacsLoading(false);
      return;
    }
    setMacsLoading(true);
    getMacsWithSows()
      .then(setMacs)
      .catch((err) => console.error("Failed to load MACs:", err))
      .finally(() => setMacsLoading(false));
  }, [macsList]);

  const fetchPositions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (selectedMAC) params.macId = selectedMAC;
      if (selectedSOW) params.sowId = selectedSOW;
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.ublStatus) params.ublStatus = filters.ublStatus;
      if (filters.quarter) params.quarter = filters.quarter;
      const data = await getPositions(params);
      setRows(Array.isArray(data) ? data : []); 
    } catch (err) {
      console.error("Failed to load positions:", err);
      setError("Failed to load positions");
    } finally {
      setLoading(false);
    }
  }, [selectedMAC, selectedSOW, filters]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  // Replace handleCapChange:
const handleCapChange = async ({ quarter, ceiling }) => {
  try {
    await setUblCeiling({ quarter, ceiling });
    const data = await getUblPoolStatus();  // ← no quarter arg
    applyPoolStatus(data);
    setTotalCap(data.ceiling);
  } catch (err) {
    console.error("Failed to set ceiling:", err);
  }
};

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key] === value ? null : value,
    }));
  };

  // Called when a new position is created — prepend to list
  const handleCreated = (newRow) => {
    setRows((prev) => [newRow, ...prev]);
  };

  // Called when an existing position is updated — replace in list
  const handleUpdated = (updated) => {
    setRows((prev) =>
      prev.map((r) =>
        (r.positionId || r.id) === (updated.positionId || updated.id)
          ? updated
          : r,
      ),
    );
  };

  const filteredRows = rows.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const allocatedName =
      typeof row.allocatedTo === "string"
        ? row.allocatedTo
        : row.allocatedTo?.fullName ?? "";
    return (
      row.positionCode?.toLowerCase().includes(q) ||
      row.roleTitle?.toLowerCase().includes(q) ||
      row.sowName?.toLowerCase().includes(q) ||
      allocatedName.toLowerCase().includes(q) ||
      row.benchedAssociateName?.toLowerCase().includes(q)
    );
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* ── Header row: TopBar + Export ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 2 }}>
        
        <Box sx={{ flex: 1 }}>
          
          <TopBar
  stats={stats}
  statsLoading={statsLoading}
  cap={cap}
  totalCap={totalCap}
  onCapChange={handleCapChange}
  onNewClick={readOnly ? undefined : () => setDrawerRow(undefined)}
  readOnly={readOnly}
/>
         
        </Box>
         <ExportButton
          label="Export Active"
          onClick={() => exportPositions(filteredRows)}
        />
      </Box>

      <PageInfo message="Create and manage Billed and Unbilled positions for your SOWs"/>

      <Box
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "210px",
            flexShrink: 0,
            borderRight: `1px solid ${t.border2}`,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <MACFilter
            macs={macs}
            loading={macsLoading}
            selectedMAC={selectedMAC}
            selectedSOW={selectedSOW}
            onSelectMAC={(id) => {
              setSelectedMAC(id);
              setSelectedSOW(null);
            }}
            onSelectSOW={(id) => setSelectedSOW(id)}
            singleMacMode={singleMacMode}
          />
        </Box>

        <Box
  sx={{
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    overflow: "hidden",
  }}
>
  <FilterBar
    filters={filters}
    onFilterChange={handleFilterChange}
    onClearAll={() => setFilters(EMPTY_FILTERS)}
    search={search}
    onSearchChange={setSearch}
  />

  {error && (
    <Box
      sx={{
        padding: "10px 20px",
        flexShrink: 0,
        fontFamily: "'DM Mono', monospace",
        fontSize: "11px",
        color: "#c84040",
        borderBottom: `1px solid ${t.border}`,
      }}
    >
      {error}
    </Box>
  )}

  {/* ✅ flex column so PositionTable gets full height */}
<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
  <PositionTable
  rows={filteredRows}
  loading={loading}
  readOnly={readOnly}
  onView={(row) => setDrawerRow({ ...row, readOnly: true })}
  onEdit={readOnly ? undefined : (row) => setDrawerRow(row)}
  activeFilter={filters.type}
  onRemove={readOnly ? undefined : async (row) => {
      const id = row.positionId ?? row.id;
      await deletePosition(id);
      setRows((prev) =>
        prev.filter((r) => (r.positionId ?? r.id) !== id),
      );
    }}
  />
</Box>
</Box>
      </Box>

      <NewPositionDrawer
        open={drawerOpen}
        onClose={() => setDrawerRow(null)}
        macs={macs}
        editRow={drawerRow}
        readOnly={drawerRow?.readOnly ?? false}
        onCreated={handleCreated}
        onUpdated={handleUpdated}
        initialMac={selectedMAC}   
        initialSow={selectedSOW}
      />
    </Box>
  );
};

export default PositionIDManagement;
